import React, { useState, useEffect, useMemo } from 'react';
import { User } from '../../types/auth';
import { userService } from '../../services/userService';
import { UserForm } from '../../components/parametres/UserForm';
import Modal from '../../components/ui/Modal';
import { useAuth } from '../../contexts/AuthContext';
import { FilterableTable } from '../../components/ui';
import type { FilterableColumnDef } from '../../components/ui';
import { PageContainer } from '../../components/layout';
import { ManageAccessModal } from '../../components/parametres/users/ManageAccessModal';
import { PASSWORD_MIN_LENGTH, PASSWORD_LABEL_SUFFIX } from '../../lib/passwordPolicy';

const SYSTEM_ROLE_BADGES: Record<string, string> = {
  regular: 'bg-blue-100 text-blue-800',
  superadmin: 'bg-purple-100 text-purple-800',
};

const SYSTEM_ROLE_LABELS: Record<string, string> = {
  regular: 'Utilisateur',
  superadmin: 'Superadmin',
};

export const UtilisateursPage: React.FC = () => {
  const { user: currentUser, refreshUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  // Reset password modal state
  const [resetPasswordUser, setResetPasswordUser] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  // Toggle active confirmation
  const [toggleUser, setToggleUser] = useState<User | null>(null);
  const [toggling, setToggling] = useState(false);

  // Manage access (PROD1.4.7 #501)
  const [manageAccessUser, setManageAccessUser] = useState<User | null>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await userService.getUsers();
      setUsers(data);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingUser(null);
    setShowUserForm(true);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setShowUserForm(true);
  };

  const handleFormSave = () => {
    setShowUserForm(false);
    if (editingUser?.id === currentUser?.id) {
      refreshUser();
    }
    setEditingUser(null);
    loadUsers();
  };

  const handleFormClose = () => {
    setShowUserForm(false);
    setEditingUser(null);
  };

  // Reset password
  const handleResetPasswordConfirm = async () => {
    if (!resetPasswordUser || !newPassword) return;
    setResetting(true);
    setResetError(null);
    try {
      await userService.resetPassword(resetPasswordUser.id, newPassword);
      setResetPasswordUser(null);
      setNewPassword('');
    } catch (err: any) {
      setResetError(err.message || 'Erreur lors de la réinitialisation');
    } finally {
      setResetting(false);
    }
  };

  // Toggle active
  const handleToggleConfirm = async () => {
    if (!toggleUser) return;
    setToggling(true);
    try {
      await userService.toggleActive(toggleUser.id);
      setToggleUser(null);
      loadUsers();
    } catch (error) {
      console.error('Error toggling user:', error);
    } finally {
      setToggling(false);
    }
  };

  // Format date-heure aligné sur la colonne « Dates » de la page Extraction
  // (fr-CA + fuseau Montréal → "2026-05-10 14:30"). fr-CA injecte des espaces
  // autour du 'h' même en h23, on reconstruit donc à partir des parts.
  const fmtDateTime = (iso?: string | null): string | null => {
    if (!iso) return null;
    const parts = new Intl.DateTimeFormat('fr-CA', {
      timeZone: 'America/Montreal',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date(iso));
    const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
    return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')}`;
  };

  const columns: FilterableColumnDef<User>[] = useMemo(() => [
    {
      key: 'full_name',
      header: 'Nom',
      sortable: true,
      searchable: true,
      render: (u) => u.full_name || '—',
    },
    {
      key: 'username',
      header: "Nom d'utilisateur",
      sortable: true,
      searchable: true,
      render: (u) => <span className="text-sm text-gray-700">{u.username}</span>,
    },
    {
      key: 'email',
      header: 'Email',
      sortable: true,
      searchable: true,
      render: (u) => u.email,
    },
    {
      key: 'system_role',
      header: 'Rôle système',
      filterable: true,
      filterValueFn: (u) => u.system_role,
      filterOptions: [
        { value: 'regular', label: 'Utilisateur' },
        { value: 'superadmin', label: 'Superadmin' },
      ],
      render: (u) => (
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${SYSTEM_ROLE_BADGES[u.system_role] || ''}`}>
          {SYSTEM_ROLE_LABELS[u.system_role] || u.system_role}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Statut',
      filterable: true,
      filterValueFn: (u) => u.is_active ? 'actif' : 'inactif',
      filterOptions: [
        { value: 'actif', label: 'Actif' },
        { value: 'inactif', label: 'Inactif' },
      ],
      render: (u) => u.is_active ? (
        <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs">Actif</span>
      ) : (
        <span className="px-2 py-0.5 bg-gray-200 text-gray-600 rounded text-xs">Inactif</span>
      ),
    },
    {
      key: 'dates',
      header: 'Dates',
      width: '180px',
      sortOptions: [
        {
          key: 'last_action',
          label: 'Dernière action',
          sortValueFn: (u) => new Date(u.last_action_at || u.last_login_at || u.created_at).getTime(),
        },
        {
          key: 'created',
          label: 'Date création',
          sortValueFn: (u) => new Date(u.created_at).getTime(),
        },
        {
          key: 'last_login',
          label: 'Dernière connexion',
          sortValueFn: (u) => (u.last_login_at ? new Date(u.last_login_at).getTime() : 0),
        },
      ],
      render: (u) => {
        const created = fmtDateTime(u.created_at);
        const login = fmtDateTime(u.last_login_at);
        const action = fmtDateTime(u.last_action_at);
        // Plus récente parmi les 3 → mise en évidence.
        const lastIso =
          u.last_action_at || u.last_login_at || u.created_at;
        const Row = ({ label, iso, value }: { label: string; iso?: string | null; value: string | null }) => {
          const isLatest = !!value && iso === lastIso;
          return (
            <div className={`flex items-baseline gap-1.5 text-xs whitespace-nowrap ${isLatest ? 'font-medium text-gray-800' : 'text-gray-500'}`}>
              <span className="w-5 text-gray-400 flex-shrink-0">{label}</span>
              <span>{value || '—'}</span>
            </div>
          );
        };
        // Ordre fixe C / Cx / A pour garder les lignes alignées d'une ligne
        // à l'autre même quand certaines dates sont absentes.
        return (
          <div className="space-y-0.5">
            <Row label="C:" iso={u.created_at} value={created} />
            <Row label="Cx:" iso={u.last_login_at} value={login} />
            <Row label="A:" iso={u.last_action_at} value={action} />
          </div>
        );
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (u) => (
        <div className="flex gap-2">
          <button onClick={() => handleEdit(u)} className="text-blue-600 hover:underline text-sm">
            Modifier
          </button>
          <button onClick={() => setManageAccessUser(u)} className="text-blue-600 hover:underline text-sm">
            Gérer accès
          </button>
          <button
            onClick={() => { setResetPasswordUser(u); setNewPassword(''); setResetError(null); }}
            className="text-orange-600 hover:underline text-sm"
          >
            Mot de passe
          </button>
          <button
            onClick={() => setToggleUser(u)}
            className={`hover:underline text-sm ${u.is_active ? 'text-red-600' : 'text-green-600'}`}
          >
            {u.is_active ? 'Désactiver' : 'Réactiver'}
          </button>
        </div>
      ),
    },
  ], []);

  return (
    <PageContainer>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Utilisateurs</h1>
        <button
          onClick={handleCreate}
          className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-600"
        >
          + Créer un utilisateur
        </button>
      </div>

      {loading ? (
        <div>Chargement...</div>
      ) : (
        <FilterableTable
          persistKey="parametres-utilisateurs"
          paginated
          data={users}
          columns={columns}
          rowKey={(u) => u.id}
          defaultSort={{ key: 'full_name', direction: 'asc' }}
          rowClassName={(u) => !u.is_active ? 'opacity-50' : ''}
          noDataMessage="Aucun utilisateur trouvé"
          emptyMessage="Aucun utilisateur ne correspond aux filtres"
        />
      )}

      {/* User Form Modal */}
      {showUserForm && (
        <UserForm
          user={editingUser}
          onClose={handleFormClose}
          onSave={handleFormSave}
        />
      )}

      {/* Reset Password Modal */}
      {resetPasswordUser && (
        <Modal
          isOpen={true}
          onClose={() => setResetPasswordUser(null)}
          title="Réinitialiser le mot de passe"
          size="sm"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Réinitialiser le mot de passe de <strong>{resetPasswordUser.full_name || resetPasswordUser.username}</strong>
            </p>
            {resetError && (
              <div className="bg-red-50 text-red-600 p-3 rounded text-sm">{resetError}</div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nouveau mot de passe * {PASSWORD_LABEL_SUFFIX}</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                minLength={PASSWORD_MIN_LENGTH}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="••••••••"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                onClick={() => setResetPasswordUser(null)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
              >
                Annuler
              </button>
              <button
                onClick={handleResetPasswordConfirm}
                disabled={resetting || newPassword.length < PASSWORD_MIN_LENGTH}
                className="px-4 py-2 text-sm bg-primary text-white rounded hover:bg-primary-600 disabled:opacity-50"
              >
                {resetting ? 'Réinitialisation...' : 'Réinitialiser'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Manage Access Modal (PROD1.4.7 #501) */}
      {manageAccessUser && (
        <ManageAccessModal
          open={true}
          user={{ id: manageAccessUser.id, email: manageAccessUser.email }}
          onClose={() => setManageAccessUser(null)}
          onSaved={() => { /* matrice sauvegardée — pas besoin de recharger users */ }}
        />
      )}

      {/* Toggle Active Confirmation Modal */}
      {toggleUser && (
        <Modal
          isOpen={true}
          onClose={() => setToggleUser(null)}
          title={toggleUser.is_active ? 'Désactiver l\'utilisateur' : 'Réactiver l\'utilisateur'}
          size="sm"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              {toggleUser.is_active
                ? <>Voulez-vous désactiver <strong>{toggleUser.full_name || toggleUser.username}</strong> ? Cet utilisateur ne pourra plus se connecter.</>
                : <>Voulez-vous réactiver <strong>{toggleUser.full_name || toggleUser.username}</strong> ?</>
              }
            </p>
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                onClick={() => setToggleUser(null)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
              >
                Annuler
              </button>
              <button
                onClick={handleToggleConfirm}
                disabled={toggling}
                className={`px-4 py-2 text-sm text-white rounded disabled:opacity-50 ${
                  toggleUser.is_active
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {toggling
                  ? 'En cours...'
                  : toggleUser.is_active ? 'Désactiver' : 'Réactiver'
                }
              </button>
            </div>
          </div>
        </Modal>
      )}
    </PageContainer>
  );
};
